"use client";

import { useRef, useState, useEffect } from "react";
import Image from "next/image";
import { getChampionImageUrl, championImageFallback } from "@/shared/lib/champion-image";

const PLACEHOLDER = "/images/champion-placeholder.png";

interface PredraftChampionImageProps {
  championId: string;
  /** Готовый адрес с CDN из манифеста (KAN-906). null — собираем сами. */
  srcOverride?: string | null;
  alt: string;
  size?: number;
  lazy?: boolean;
  className?: string;
  fill?: boolean;
}

export function PredraftChampionImage({
  championId,
  srcOverride = null,
  alt,
  size = 40,
  lazy = true,
  className,
  fill = false,
}: PredraftChampionImageProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(!lazy);

  useEffect(() => {
    if (!lazy || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setInView(true);
      },
      { rootMargin: "100px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [lazy]);

  const src = championId
    ? inView
      ? (srcOverride ?? getChampionImageUrl(championId))
      : PLACEHOLDER
    : PLACEHOLDER;

  const containerClass = fill
    ? `relative w-full h-full overflow-hidden ${className ?? ""}`
    : `relative shrink-0 overflow-hidden rounded ${className ?? ""}`;
  const containerStyle = fill ? undefined : { width: size, height: size };

  return (
    <div ref={ref} className={containerClass} style={containerStyle}>
      <Image
        src={src}
        alt={alt}
        width={fill ? undefined : size}
        height={fill ? undefined : size}
        fill={fill}
        unoptimized
        className="object-cover"
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          // KAN-889: сначала прокси бэкенда, и только потом заглушка. Раньше первая
          // же ошибка отправляла картинку в placeholder — а пустой CDN (новый патч,
          // ещё не опубликованные файлы) это ошибка для КАЖДОГО чемпиона, то есть
          // вся сетка выбора становилась серыми квадратами при живом источнике.
          const t = e.currentTarget;
          if (!t || !championId) return;
          const proxied = championImageFallback("champion", championId);
          if (t.src.endsWith(proxied)) {
            if (!t.src.endsWith(PLACEHOLDER)) t.src = PLACEHOLDER;
            return;
          }
          if (!t.src.endsWith(PLACEHOLDER)) t.src = proxied;
        }}
      />
    </div>
  );
}
